/**
 * Purchasing: the requisitions waiting on a decision, and the orders placed.
 *
 * The approval chain is shown as a chain rather than as a status word. Who
 * approved, under what authority and up to what limit is the part an auditor
 * asks for, and a screen that shows only "APPROVED" has kept the answer and
 * thrown away the evidence.
 *
 * The authority a person holds is entered here rather than read from the
 * platform, and the screen says so. Access levels exist on the platform, but
 * they say what somebody may open, not what value of purchase they may commit
 * the airport to — and the second is not derivable from the first.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  decideRequisition,
  orderFromRequisition,
  requisitionValue,
  type Actor,
  type Authority,
  type Result,
} from "../../domain/storesEdit";
import type { StoresRegister } from "../../domain/stores";
import type { StoresDesk } from "../../domain/storesDesk";
import { Button, Card, EmptyState, Field, Input, Select, Table } from "../../design/components";
import { dueText } from "./StoresPage";
import { t, tn } from "../../i18n";
import s from "./Stores.module.css";

/**
 * The authorities, and the limits they hold. Every one of them PROPOSED.
 *
 * No airport has agreed these figures with us. They are a starting point that
 * makes the chain demonstrable, and the screen says as much beside them — an
 * unagreed limit presented as a policy is how a demonstration becomes a
 * commitment somebody later has to explain.
 */
export const AUTHORITIES: Authority[] = [
  { role: "Storekeeper", limit: 5_000 },
  { role: "Maintenance Manager", limit: 50_000 },
  { role: "Director of Operations", limit: 250_000 },
  { role: "Chief Executive", limit: null },
];

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  submitted: "Waiting on a decision",
  approved: "Approved, not yet ordered",
  rejected: "Rejected",
  ordered: "Ordered",
  cancelled: "Canceled",
  open: "Open",
  part_received: "Part received",
  received: "Complete",
};

export function PurchasingCard({
  desk,
  register,
  who,
  onApply,
  onReceive,
}: {
  desk: StoresDesk;
  register: StoresRegister;
  who: string;
  onApply: (r: Result) => void | Promise<void>;
  onReceive: (orderNumber: string) => void;
}) {
  const actor: Actor = { name: who };
  const [role, setRole] = useState(AUTHORITIES[1].role);
  const [ordering, setOrdering] = useState<string | null>(null);
  const authority = AUTHORITIES.find((a) => a.role === role) ?? AUTHORITIES[0];
  const waiting = register.requisitions.filter((q) => q.status === "submitted");
  const approved = register.requisitions.filter((q) => q.status === "approved");
  const live = register.orders.filter((o) => o.status === "open" || o.status === "part_received");

  return (
    <Card
      collapsible
      title="Purchasing"
      sub="Requests, approvals and supplier deliveries"
    >
      {desk.overdue.length > 0 && (
        <p className={s.note} data-testid="stores-overdue">
          {tn(
            desk.overdue.length,
            "{n} purchase order was promised before today and has not arrived.",
            "{n} purchase orders were promised before today and have not arrived.",
          )}
        </p>
      )}

      <div className={s.filters}>
        <Field label={t("Deciding as")}>
          <Select value={role} onChange={(e) => setRole(e.target.value)} aria-label={t("Deciding as")}>
            {AUTHORITIES.map((a) => (
              <option key={a.role} value={a.role}>
                {a.limit === null
                  ? t("{role} — no limit", { role: a.role })
                  : t("{role} — up to {limit}", { role: a.role, limit: a.limit.toLocaleString() })}
              </option>
            ))}
          </Select>
        </Field>
        <p className={s.meta}>
          {t(
            "These limits are proposed, not airport policy.",
          )}
        </p>
      </div>

      <h3 className={s.groupName}>{t("Waiting on a decision")}</h3>
      {waiting.length === 0 ? (
        <EmptyState>{t("No requisition is waiting on a decision.")}</EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t("Requisition")}</th>
              <th>{t("For")}</th>
              <th className={s.num}>{t("Value")}</th>
              <th>{t("Raised by")}</th>
              <th>{t("Decision")}</th>
            </tr>
          </thead>
          <tbody data-testid="stores-requisitions">
            {waiting.map((q) => {
              const value = requisitionValue(q, register);
              return (
                <tr key={q.number}>
                  <td className={s.sku}>{q.number}</td>
                  <td>
                    {q.lines.map((l) => (
                      <div key={l.sku}>
                        {`${l.qty} × ${l.sku}`}
                        <div className={s.why}>{l.why}</div>
                      </div>
                    ))}
                  </td>
                  <td className={s.num}>
                    {value === null ? (
                      <span className={s.unknown}>{t("unknown")}</span>
                    ) : (
                      `${Math.round(value).toLocaleString()} ${register.currency}`
                    )}
                  </td>
                  <td>
                    {q.raisedBy}
                    <div className={s.why}>{q.department ?? t("no department recorded")}</div>
                  </td>
                  <td>
                    <div className={s.actions}>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() =>
                          void onApply(
                            decideRequisition(register, { number: q.number, decision: "approved", authority }, actor),
                          )
                        }
                      >
                        {t("Approve")}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() =>
                          void onApply(
                            decideRequisition(register, { number: q.number, decision: "rejected", authority }, actor),
                          )
                        }
                      >
                        {t("Reject")}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {approved.length > 0 && (
        <>
          <h3 className={s.groupName}>{t("Approved, waiting to be ordered")}</h3>
          <Table>
            <thead>
              <tr>
                <th>{t("Requisition")}</th>
                <th>{t("For")}</th>
                <th>{t("Approved by")}</th>
                <th>{t("Order")}</th>
              </tr>
            </thead>
            <tbody>
              {approved.map((q) => (
                <tr key={q.number}>
                  <td className={s.sku}>{q.number}</td>
                  <td>{q.lines.map((l) => `${l.qty} × ${l.sku}`).join(", ")}</td>
                  <td>
                    {q.approvals.map((a) => (
                      <div key={a.at}>
                        {a.by}
                        <div className={s.why}>
                          {a.limit === null
                            ? t("{role}, no limit", { role: a.role })
                            : t("{role}, up to {limit}", { role: a.role, limit: a.limit.toLocaleString() })}
                        </div>
                      </div>
                    ))}
                  </td>
                  <td>
                    <Button size="sm" onClick={() => setOrdering(q.number)}>
                      {t("Raise the order")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      <h3 className={s.groupName}>{t("On order")}</h3>
      {live.length === 0 ? (
        <EmptyState>{t("Nothing is on order.")}</EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t("Order")}</th>
              <th>{t("Supplier")}</th>
              <th>{t("Outstanding")}</th>
              <th>{t("Expected")}</th>
              <th>{t("State")}</th>
              <th>{t("Receive")}</th>
            </tr>
          </thead>
          <tbody data-testid="stores-orders">
            {live.map((o) => {
              const outstanding = o.lines.filter((l) => l.received < l.qty);
              const late = desk.overdue.some((x) => x.number === o.number);
              return (
                <tr key={o.number}>
                  <td className={s.sku}>
                    {o.number}
                    {o.requisitionNumber && <div className={s.why}>{t("from {n}", { n: o.requisitionNumber })}</div>}
                    {o.raisedElsewhere && (
                      /* not an order this register approved, and it says so */
                      <div className={s.why}>{t("raised in {system}", { system: o.raisedElsewhere.system })}</div>
                    )}
                  </td>
                  <td>
                    {o.supplier}
                    {o.contractRef && <div className={s.why}>{t("under {ref}", { ref: o.contractRef })}</div>}
                  </td>
                  <td>
                    {outstanding.map((l) => (
                      <div key={l.sku} className={s.sku}>{`${l.qty - l.received} × ${l.sku}`}</div>
                    ))}
                  </td>
                  <td className={late ? s.bad : undefined}>
                    {o.expectedAt ? dueText(o.expectedAt) : <span className={s.unknown}>{t("no date given")}</span>}
                  </td>
                  <td>{t(STATUS_LABEL[o.status] ?? o.status)}</td>
                  <td>
                    <Button size="sm" onClick={() => onReceive(o.number)}>
                      {t("Book in")}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {ordering && (
        <OrderDialog
          register={register}
          number={ordering}
          who={who}
          onClose={() => setOrdering(null)}
          onApply={onApply}
        />
      )}
    </Card>
  );
}

function OrderDialog({
  register,
  number,
  who,
  onClose,
  onApply,
}: {
  register: StoresRegister;
  number: string;
  who: string;
  onClose: () => void;
  onApply: (r: Result) => void | Promise<void>;
}) {
  const q = register.requisitions.find((x) => x.number === number);
  const suggested = q?.lines.map((l) => register.items.find((i) => i.sku === l.sku)?.supplier).find(Boolean) ?? "";
  const lead = Math.max(
    0,
    ...(q?.lines.map((l) => register.items.find((i) => i.sku === l.sku)?.leadDays ?? 0) ?? [0]),
  );
  const due = new Date();
  due.setDate(due.getDate() + lead);
  const [supplier, setSupplier] = useState(suggested);
  const [expectedAt, setExpectedAt] = useState(due.toISOString().slice(0, 10));

  return (
    <Dialog onClose={onClose} testId="stores-order-dialog" title={t("Raise the order for {number}", { number })}>
      <div className={s.formRow}>
        <Field label={t("Supplier")}>
          <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} aria-label={t("Supplier")} />
        </Field>
        <Field label={t("Expected")}>
          <Input
            type="date"
            value={expectedAt}
            onChange={(e) => setExpectedAt(e.target.value)}
            aria-label={t("Expected")}
          />
        </Field>
      </div>
      <p className={s.meta}>
        {lead > 0
          ? t("The date offered is today plus the longest lead time recorded on the lines, {n}. It is a supplier's promise once they give one, not a forecast.", {
              n: tn(lead, "{n} day", "{n} days"),
            })
          : t("No lead time is recorded on these lines, so no date is offered. Enter what the supplier promises.")}
      </p>
      <div className={s.formFoot}>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button
          variant="primary"
          onClick={() => {
            void onApply(
              orderFromRequisition(register, { number, supplier, expectedAt: expectedAt || undefined }, { name: who }),
            );
            onClose();
          }}
        >
          {t("Raise the order")}
        </Button>
      </div>
    </Dialog>
  );
}

/**
 * The same native dialog the rest of the application uses: `showModal` gives
 * the focus trap, the escape key and the backdrop without any of it being
 * reimplemented here, and no close() on cleanup for the reason recorded in
 * CreateWorkOrderDialog.
 */
export function Dialog({
  title,
  children,
  onClose,
  testId,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  testId?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  return (
    <dialog ref={ref} onClose={onClose} className={s.dialog} data-testid={testId}>
      <div className={s.form}>
        <h2>{title}</h2>
        {children}
      </div>
    </dialog>
  );
}
